import { narrowTo } from '../../../core/api/vocabulary';
import { calendarDayIn, isoDay } from '../../../core/time/calendar-day';
import { MONTH_NAMES, WEEK_DAY_NAMES } from '../../recurrence/pattern-words';
/**
 * Two kinds of date, formatted two different ways — and the difference is not cosmetic.
 *
 * A due date or a defer date is **day-valued**: quick-add's `<2026-09-01` carries no time, and the
 * API stores it as midnight UTC of that day. Formatted with the viewer's own zone it reads as
 * 31 August for everyone west of UTC. Day-valued dates are therefore always read in UTC — the same
 * rule `date-cell.ts` applies when it decides urgency.
 *
 * A completion is an **instant**: it happened at a moment, and the right thing to show is that
 * moment in **the practitioner's** zone — which 010 made a thing the caller has to supply. It was
 * the browser's zone before, by omission; the two are the same until a practitioner sets a zone or
 * carries a laptop somewhere, and then they are a day apart for part of every day (010 FR-008).
 *
 * 015 added the third axis: **how** the day is written — the practitioner's date-format family
 * (FR-002/FR-003). The four families' three lengths are defined here, once, and nowhere else. Like
 * `zone`, `family` is required rather than defaulted, and for the same documented reason: a default
 * would be `worded`, silently, at every call site that forgot, and a signature that compiles is not
 * evidence that anyone thought about it. Neither new axis moves either old rule: the family changes
 * the writing of a date, never which calendar day it names (FR-004).
 */
export const DATE_FORMAT_FAMILIES = ['worded', 'day-month-year', 'month-day-year', 'iso'] as const;
export type DateFormatFamily = (typeof DATE_FORMAT_FAMILIES)[number];

const day: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', timeZone: 'UTC' };
const fullDay: Intl.DateTimeFormatOptions = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
const shortDay: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };

/**
 * The fixed English weekday table for the numeric families' long forms (research R-006): locale is
 * cut (spec §Cut deliberately), so the weekday comes from a table, not from a locale.
 */
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

interface DateParts {
  readonly year: string;
  readonly month: string;
  readonly dayOfMonth: string;
  readonly weekday: string;
}

/**
 * The date parts of an instant in a zone, as zero-padded Latin digits. The numeric families are
 * constructed from these parts, never from locale formatting: `05/09/2026` must not become
 * `5/9/2026` or `05.09.2026` because the browser's locale leaned that way (R-006). The extraction
 * locale is pinned (`en-US`) so the digits are deterministic; it never chooses a rendering — the
 * templates below do. The weekday is computed in UTC from the *already zone-projected* parts, so
 * it names the same day the digits do.
 */
const partsIn = (iso: string, zone: string): DateParts => {
  // Through `calendarDayIn` rather than a formatter of this file's own. The first draft constructed
  // an `Intl.DateTimeFormat` with the SAME four options `core/time/calendar-day.ts` already
  // constructs — a second projection of an instant onto a calendar day, which is the exact thing
  // `no-second-reduction.spec.ts` exists to prevent, and it was invisible to that suite because it
  // uses no local getter. It also built one formatter per call, on every dated row of every list,
  // where the shared one is cached per zone.
  const day = calendarDayIn(new Date(iso), zone);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return {
    year: String(day.getUTCFullYear()),
    month: pad(day.getUTCMonth() + 1),
    dayOfMonth: pad(day.getUTCDate()),
    // Read off the projected day itself: it IS midnight UTC of the day the digits name, so the
    // weekday cannot disagree with them.
    weekday: WEEKDAYS[day.getUTCDay()],
  };
};

type NumericFamily = Exclude<DateFormatFamily, 'worded'>;

/**
 * The numeric forms, as **total** `Record`s over `NumericFamily` — `settings-page.ts`'s
 * `DATE_FORMAT_LABELS` construction, for its reason (`/code-review` finding 7). These were chained
 * ternaries ending in an unguarded `else`, which meant a fifth family added to
 * `DATE_FORMAT_FAMILIES` would have widened `NumericFamily`, fallen through to that `else` and
 * rendered as ISO — silently, with nothing failing to compile. A missing key here is a type error.
 *
 * The ISO short is month-day, matching the Worded short's scope rather than truncating the ISO
 * medium from the left.
 */
const numericMediumBy: Record<NumericFamily, (p: DateParts) => string> = {
  'day-month-year': (p) => `${p.dayOfMonth}/${p.month}/${p.year}`,
  'month-day-year': (p) => `${p.month}/${p.dayOfMonth}/${p.year}`,
  iso: (p) => `${p.year}-${p.month}-${p.dayOfMonth}`,
};
const numericShortBy: Record<NumericFamily, (p: DateParts) => string> = {
  'day-month-year': (p) => `${p.dayOfMonth}/${p.month}`,
  'month-day-year': (p) => `${p.month}/${p.dayOfMonth}`,
  iso: (p) => `${p.month}-${p.dayOfMonth}`,
};

const numericMedium = (p: DateParts, family: NumericFamily): string => numericMediumBy[family](p);
const numericShort = (p: DateParts, family: NumericFamily): string => numericShortBy[family](p);
/** Every numeric long form is the weekday and the medium form — one rule, not three spellings. */
const numericLong = (p: DateParts, family: NumericFamily): string => `${p.weekday} ${numericMedium(p, family)}`;

/**
 * Day-valued dates, in the practitioner's family. The Worded arms execute the exact `Intl` calls
 * this file made before 015 — same option objects, same `undefined` locale — so the default's
 * output is byte-identical by construction, not by re-derivation (R-006; the dates.spec.ts golden).
 */
export const formatDay = (iso: string, family: DateFormatFamily): string =>
  family === 'worded' ? new Date(iso).toLocaleDateString(undefined, day) : numericMedium(partsIn(iso, 'UTC'), family);
export const formatDayLong = (iso: string, family: DateFormatFamily): string =>
  family === 'worded' ? new Date(iso).toLocaleDateString(undefined, fullDay) : numericLong(partsIn(iso, 'UTC'), family);
export const formatDayShort = (iso: string, family: DateFormatFamily): string =>
  family === 'worded' ? new Date(iso).toLocaleDateString(undefined, shortDay) : numericShort(partsIn(iso, 'UTC'), family);

/**
 * The same day-valued date as a `<input type="date">` value — the inverse of `dayToIso` just
 * below, and UTC for the reason above: the instant is midnight UTC of the day it means, so any
 * local reduction of it is a day early for half the world. 012 needs it to open the recurrence form
 * on the day an adopted action already carries. No `family`: a control's value is a wire format,
 * not a display (015 FR-016).
 */
export const isoToDay = (iso: string): string => isoDay(new Date(iso));

/**
 * The inverse: a `<input type="date">` value back to the instant the API stores for that day.
 * Midnight UTC, for the reason `isoToDay` reads in UTC — a day-valued date carries no time, and a
 * local-midnight lift would store the previous day for everyone east of UTC.
 *
 * It lived in `defer.ts` while the defer menu was its only caller; 016's due-date control is the
 * second, and a seam only one caller can find is how a third hand-written conversion appears
 * (research R-010). Nothing about the function changed in the move.
 */
export const dayToIso = (day: string): string => `${day}T00:00:00.000Z`;

/**
 * An instant, in the practitioner's zone (010 FR-008) and family (015 FR-003).
 *
 * `zone` is required rather than defaulted. A default would be the browser's zone, silently, at
 * every call site that forgot — which is exactly the state 010 found the application in, and a
 * signature that compiles is not evidence that anyone thought about it. `family` is required for
 * the same reason. The zone picks the calendar day; the family only writes it (FR-004).
 */
export const formatInstant = (iso: string, zone: string, family: DateFormatFamily): string =>
  family === 'worded'
    ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', timeZone: zone })
    : numericLong(partsIn(iso, zone), family);
export const formatInstantShort = (iso: string, zone: string, family: DateFormatFamily): string =>
  family === 'worded'
    ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: zone })
    : numericShort(partsIn(iso, zone), family);

/**
 * Narrows the wire's plain string to a family (data-model §5). The generated contract carries
 * `dateFormat` as a plain string, so this is where an unrecognised spelling is refused — and the
 * refusing is `core/api/vocabulary.ts`'s, not this file's: three of these seams were written by
 * hand in one cycle and the third disagreed with the other two. The sentence it throws is
 * unchanged.
 */
export const asDateFormatFamily = narrowTo(DATE_FORMAT_FAMILIES, 'date format family');

/**
 * The three title-date variants (015 FR-005; research R-007) — the masthead's formatter, beside
 * the families because this is the one formatting module. Separate from the family on purpose:
 * Tracks keeps `date_format` and `title_date_format` apart, and the masthead is the variant's one
 * surface. The full-name tables are fixed English for R-006's reason — locale is cut, so the
 * words come from a table, never from the runtime's locale — and the three renderings are pinned
 * by FR-005 punctuation included, which is what `dates.spec.ts` holds them to. The full-name
 * tables themselves live in `shared/recurrence/pattern-words.ts` and are imported, not restated.
 */
export const TITLE_DATE_VARIANTS = ['weekday-day-month-year', 'weekday-month-day-year', 'iso-weekday'] as const;
export type TitleDateVariant = (typeof TITLE_DATE_VARIANTS)[number];


/**
 * A calendar day, in the practitioner's title variant. `day` is a day string (`isoDay`'s output,
 * `YYYY-MM-DD`): the masthead's day has already been reduced by the home store's `calendarDayIn`,
 * so this function writes a day and never chooses one — handing it an instant would reopen the
 * exact ambiguity 010 closed. The worded variants carry the day-of-month unpadded, as the Worded
 * family's long form does; the ISO variant keeps the wire's padding.
 */
export const formatTitleDate = (day: string, variant: TitleDateVariant): string => {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  // `WEEK_DAY_NAMES` is ISO-ordered (Monday = index 0) and `getUTCDay()` is Sunday = 0, so Sunday
  // wraps to the end. The tables are `shared/recurrence/pattern-words.ts`'s rather than copies: that
  // file states in its own comment that it exports them so there is one spelling of "Wednesday" in
  // the application, and 015 had quietly made a second one four lines from here.
  const weekday = WEEK_DAY_NAMES[(new Date(Date.UTC(year, month - 1, dayOfMonth)).getUTCDay() + 6) % 7];
  return variant === 'weekday-day-month-year' ? `${weekday}, ${dayOfMonth} ${MONTH_NAMES[month - 1]} ${year}`
    : variant === 'weekday-month-day-year' ? `${weekday}, ${MONTH_NAMES[month - 1]} ${dayOfMonth}, ${year}`
    : `${day} ${weekday}`;
};

/** The variants' narrowing seam — the same one, given a second vocabulary and a second label. */
export const asTitleDateVariant = narrowTo(TITLE_DATE_VARIANTS, 'title date variant');
